import { Box } from "@chakra-ui/react";
import { FullPageLoader } from "@components/index";
import useWebConfig from "hooks/useWebConfig";
import { ReactNode, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { loadConfigAsync } from "store/slices/configSlice";

/**
 * Renders the config-driven site chrome — header (top nav + mobile bottom nav)
 * and footer — around arbitrary content, mirroring the layout that
 * `withSiteConfig` applies to regular pages.
 *
 * Use for standalone pages that aren't tied to a backend `page_type` (e.g. the
 * 404 page) so they still get the tenant's navigation. Loads the config from
 * the backend if it hasn't been initialized yet.
 */
function SiteChrome({ children }: { children: ReactNode }) {
  const { configInitialized, configLoadFail, ...config } = useAppSelector(
    (state) => state.config,
  );
  const dispatch = useAppDispatch();
  const { headerElements, footerElements } = useWebConfig({
    config,
    page_type: null,
  });

  useEffect(() => {
    if (!configInitialized) dispatch(loadConfigAsync());
  }, [dispatch, configInitialized]);

  const isBottomNav =
    config.layout_config?.mobile_config?.nav_style === "bottom_nav";

  // Show a loader while the config is loading so nav doesn't pop in late.
  // If the config fails to load we still render the chrome (header/footer will
  // simply be empty) so the page content is never blocked.
  if (!configInitialized && !configLoadFail) {
    return <FullPageLoader />;
  }

  return (
    <Box
      sx={{
        backgroundImage: config.layout_config?.background_image
          ? `${config.layout_config?.linear_gradient ? `${config.layout_config.linear_gradient} ,` : ""}url(${config.layout_config.background_image})`
          : config.layout_config?.background,
        background: config.layout_config?.background,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100dvh",
        imageRendering: "initial",
      }}
    >
      {headerElements}
      <Box
        as="main"
        className="app-page-main"
        sx={{ minHeight: "70vh", position: "relative" }}
        // Reserve space for the fixed mobile bottom nav so content isn't hidden.
        pb={
          isBottomNav
            ? { base: "calc(4rem + env(safe-area-inset-bottom))", xl: 0 }
            : undefined
        }
      >
        {children}
      </Box>
      {isBottomNav ? (
        // Footer moves into the bottom-nav "More" sheet on mobile.
        <Box display={{ base: "none", xl: "block" }}>{footerElements}</Box>
      ) : (
        footerElements
      )}
    </Box>
  );
}

export default SiteChrome;
